import React, { useState, useEffect } from 'react';
import { AddSelectionGroupToItem } from '../../../../../../../../../../crud/menu_api/selection_groups';
import { Modal, InputGroup, FormControl, ListGroup, Button, Badge } from 'react-bootstrap';
import { getAllItemsByLocalId } from '../../../../../../../../../../crud/menu_api/items';
import { toast } from 'react-toastify';
import './AddGroupToItemModal.scss';
import { X } from 'react-bootstrap-icons';
import { FaSearch, FaCheck, FaPlus } from 'react-icons/fa';
import { formatCurrency } from '../../../../../../../../../../utils/functions';
import FilterDropdown from 'views/Restaurant/Menu/Menu/components/Items/components/Table/components/FilterDropdown/FilterDropdown';

interface SelectionGroupType {
  readonly selection_group_id: number;
  selection_group_name: string;
  items_contained?: { item_id: number; item_name: string; }[];
  items_affected?: { item_id: number; item_name: string; }[];
}

interface AddGroupToItemModalProps {
  show: boolean;
  handleClose: () => void;
  sg: SelectionGroupType;
  setItemSelections: (items: any[]) => void;
  itemSelections: any[];
}

interface ItemType {
  item_id: number;
  item_name: string;
  label: string;
  local_id: number;
  price: number;
  price_delivery: number;
  price_online: number;
  price_desk: number;
  category_id?: number;
  category_name?: string;
}

const AddGroupToItemModal: React.FC<AddGroupToItemModalProps> = ({
  show,
  handleClose,
  sg,
  setItemSelections,
  itemSelections,
}) => {
  const [allItems, setAllItems] = useState<ItemType[]>([]);
  const [availableItems, setAvailableItems] = useState<ItemType[]>([]);
  const [filteredItems, setFilteredItems] = useState<ItemType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState<ItemType[]>([]);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState<boolean>(true);

  // Categories for filtering
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);

  // Fetch raw items only when modal opens
  useEffect(() => {
    if (!show) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user ? user.local_id : 0;
    setLoadingItems(true);
    getAllItemsByLocalId(localId)
      .then((r) => {
        setAllItems(r.data || []);
        // Extract unique categories from items
        const categories = new Map();
        (r.data || []).forEach((item: any) => {
          if (item.category_id && item.category_name) {
            categories.set(item.category_id, {
              category_id: item.category_id,
              category_name: item.category_name
            });
          }
        });
        setAllCategories(Array.from(categories.values()));
      })
      .catch((err) => console.error('Error fetching items', err))
      .finally(() => setLoadingItems(false));
  }, [show]);

  // Derive available items (exclude existing and circular references)
  useEffect(() => {
    const excludedIds = new Set([
      ...(sg.items_contained || []).map(item => item.item_id),
      ...(sg.items_affected || []).map(item => item.item_id),
      ...itemSelections.map(item => item.item_id)
    ]);
    setAvailableItems(allItems.filter((it: ItemType) => !excludedIds.has(it.item_id)));
  }, [allItems, sg.items_contained, sg.items_affected, itemSelections]);

  // Normalization helper (remove accents, spaces, convert to lowercase)
  const normalize = (str: string) =>
    (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/\s+/g, ' ')
      .trim();

  // Filter items based on search query and categories
  useEffect(() => {
    let base = availableItems || [];
    
    // Filter by categories first
    if (selectedCategories.length > 0) {
      const selectedCategoryIds = selectedCategories.map(cat => cat.value);
      base = base.filter((item: ItemType) => selectedCategoryIds.includes(item.category_id));
    }
    
    // Then filter by search query
    const q = normalize(searchQuery);
    if (!q) { 
      setFilteredItems(base); 
      return; 
    }
    
    const tokens = q.split(' ').filter(Boolean);
    const filtered = base.filter((item: ItemType) => {
      const haystack = normalize(item?.item_name + ' ' + item?.label);
      return tokens.every(tok => haystack.includes(tok));
    });
    setFilteredItems(filtered);
  }, [searchQuery, availableItems, selectedCategories]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleItemSelect = (item: ItemType) => {
    const isAlreadySelected = selectedItems.some(selected => selected.item_id === item.item_id);
    
    if (isAlreadySelected) {
      setSelectedItems(selectedItems.filter(selected => selected.item_id !== item.item_id));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleRemoveSelectedItem = (itemId: number) => {
    setSelectedItems(selectedItems.filter(item => item.item_id !== itemId));
  };

  const isItemSelected = (itemId: number) => {
    return selectedItems.some(item => item.item_id === itemId);
  };

  const handleConfirmAdd = () => {
    if (selectedItems.length === 0) {
      toast.error('No se ha seleccionado ningún producto');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setConfirmLoading(true);

    // Create promises for all selected items with default values
    const addPromises = selectedItems.map(item => {
      const payload = {
        selection_group_id: sg.selection_group_id,
        item_id: item.item_id,
        max_selections: 1,
        min_selections: 0,
        max_variance: 1,
        min_variance: 0,
        local_id: user ? user.local_id : 0,
      };

      return AddSelectionGroupToItem(payload);
    });

    Promise.all(addPromises)
      .then((responses) => {
        toast.success(`${selectedItems.length} producto${selectedItems.length > 1 ? 's' : ''} vinculado${selectedItems.length > 1 ? 's' : ''} al grupo`);
        
        // Add all new items to the item selections state
        const newItems = responses.map(response => (response as any).data);
        setItemSelections([...itemSelections, ...newItems]);
        
        handleModalClose();
      })
      .catch((error) => {
        console.error('Error vinculando productos al grupo', error);
        const message = error.response?.data?.message || 'Error vinculando productos al grupo';
        toast.error(message);
      })
      .finally(() => {
        setConfirmLoading(false);
      });
  };

  const handleAddAllFromCategory = () => {
    if (selectedCategories.length === 0) return;
    
    const selectedCategoryIds = selectedCategories.map(cat => cat.value);
    const categoryItems = availableItems.filter((item: ItemType) => 
      selectedCategoryIds.includes(item.category_id)
    );
    
    if (categoryItems.length === 0) {
      toast.info('No hay productos disponibles en las categorías seleccionadas');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setConfirmLoading(true);

    // Create promises for all category items
    const addPromises = categoryItems.map(item => {
      const payload = {
        selection_group_id: sg.selection_group_id,
        item_id: item.item_id,
        max_selections: 1,
        min_selections: 0,
        max_variance: 1,
        min_variance: 0,
        local_id: user ? user.local_id : 0,
      };

      return AddSelectionGroupToItem(payload);
    });

    Promise.all(addPromises)
      .then((responses) => {
        const categoryNames = selectedCategories.map(cat => cat.label).join(', ');
        const message = selectedCategories.length === 1 
          ? `${categoryItems.length} productos de la categoría "${categoryNames}" vinculados al grupo`
          : `${categoryItems.length} productos de las categorías "${categoryNames}" vinculados al grupo`;
        toast.success(message);
        
        // Add all new items to the item selections state
        const newItems = responses.map(response => (response as any).data);
        setItemSelections([...itemSelections, ...newItems]);
        
        handleModalClose();
      })
      .catch((error) => {
        console.error('Error vinculando productos de categorías al grupo', error);
        const message = error.response?.data?.message || 'Error vinculando productos al grupo';
        toast.error(message);
      })
      .finally(() => {
        setConfirmLoading(false);
      });
  };

  const handleModalClose = () => {
    if (confirmLoading) return; // Don't close while adding
    setSearchQuery('');
    setSelectedItems([]);
    setSelectedCategories([]);
    handleClose();
  };

  // Highlight matched tokens in text
  const highlightMatches = (text: string) => {
    if (!searchQuery.trim()) return text;
    const tokens = normalize(searchQuery).split(' ').filter(Boolean);
    if (!tokens.length) return text;
    const norm = (s: string) => normalize(s);
    let remaining = text;
    const out: React.ReactNode[] = [];
    while (remaining.length) {
      let earliest = -1; let token = '';
      for (const t of tokens) {
        const idx = norm(remaining).indexOf(t);
        if (idx !== -1 && (earliest === -1 || idx < earliest)) { earliest = idx; token = t; }
      }
      if (earliest === -1) { out.push(remaining); break; }
      if (earliest > 0) out.push(remaining.slice(0, earliest));
      const matchPortion = remaining.slice(earliest, earliest + token.length);
      out.push(<mark key={out.length} className="aig-hl">{matchPortion}</mark>);
      remaining = remaining.slice(earliest + token.length);
    }
    return <>{out}</>;
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="lg" className="add-group-to-item-modal">
      <Modal.Header>
        <div className='form-modal-title-container'>
          <Modal.Title className='form-modal-title'>Vincular a {sg.selection_group_name}</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleModalClose} disabled={confirmLoading}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body className="aig-body">
        <div className="aig-item-selector">
          {/* Barra de Búsqueda y Filtro de Categorías */}
          <div className="aig-search-container">
            <div className="aig-search-row">
              <div className="aig-search-wrapper">
                <InputGroup className="aig-search-input">
                  <InputGroup.Text className="aig-search-icon">
                    <FaSearch size={10} />
                  </InputGroup.Text>
                  <FormControl
                    type="text"
                    placeholder="Buscar por nombre o etiqueta..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    autoFocus
                    className="aig-search-field"
                    autoComplete="off"
                    disabled={confirmLoading}
                  />
                </InputGroup>
              </div>
              
              {/* Category Filter Dropdown */}
              <div className="aig-category-filter-wrapper">
                <FilterDropdown
                  options={allCategories.map(cat => ({
                    value: cat.category_id,
                    label: cat.category_name
                  }))}
                  selectedItems={selectedCategories}
                  onChange={setSelectedCategories}
                  placeholder="Filtrar por categorías"
                />
              </div>
            </div>
            
            {/* Add All from Category Button */}
            {selectedCategories.length > 0 && (
              <div className="aig-add-all-button-row">
                <Button
                  variant="none"
                  size="sm"
                  onClick={handleAddAllFromCategory}
                  className="aig-add-all-category-btn"
                  type="button"
                  disabled={confirmLoading}
                >
                  <FaPlus className="me-1" />
                  Vincular todos los productos de {selectedCategories.length === 1 ? 'la categoría' : 'las categorías'}
                </Button>
              </div>
            )}
          </div>

          {/* Selected Items Badges */}
          {selectedItems.length > 0 && (
            <div className="aig-selected-items-section">
              <div className="aig-selected-items-badges">
                {selectedItems.map((item) => (
                  <Badge 
                    key={item.item_id} 
                    className="aig-selected-item-badge"
                    bg="secondary"
                  >
                    <span className="aig-badge-text">{item.item_name}</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="aig-badge-remove-btn"
                      onClick={() => handleRemoveSelectedItem(item.item_id)}
                      type="button"
                    >
                      <X size={12} />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Lista de Ítems Filtrados */}
          <div className="aig-search-results">
            {loadingItems ? (
              <div className="aig-loading-state">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Buscando...
              </div>
            ) : filteredItems.length > 0 ? (
              <>
                {searchQuery && (
                  <div className="aig-results-count">
                    {filteredItems.length} resultado{filteredItems.length !== 1 ? 's' : ''}
                  </div>
                )}
                
                <ListGroup className="aig-items-list">
                  {filteredItems.map((item) => (
                    <ListGroup.Item
                      key={item.item_id}
                      action
                      onClick={() => handleItemSelect(item)}
                      className={`aig-item-option ${isItemSelected(item.item_id) ? 'aig-selected' : ''}`}
                      disabled={confirmLoading}
                    >
                      <div className="aig-item-content">
                        <div className="aig-item-main-info">
                          <div className="aig-item-name-container">
                            <span className="aig-item-name">
                              {highlightMatches(item.item_name)}
                            </span>
                            {item.label && item.label !== item.item_name && (
                              <span className="aig-item-label"> • {highlightMatches(item.label)}</span>
                            )}
                          </div>
                        </div>
                        <div className="aig-item-price">
                          {item.price > 0 ? formatCurrency(item.price) : (
                            <span className="aig-free-price">Gratis</span>
                          )}
                        </div>
                        {isItemSelected(item.item_id) && (
                          <div className="aig-selected-indicator">
                            <FaCheck />
                          </div>
                        )}
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            ) : searchQuery ? (
              <div className="aig-no-results">
                Sin resultados para "{searchQuery}"
              </div>
            ) : (
              <div className="aig-empty-search">
                Escribe para buscar productos...
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="aig-selector-actions">
            <Button
              variant="secondary"
              onClick={handleModalClose}
              className="aig-cancel-btn"
              type="button"
              disabled={confirmLoading}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmAdd}
              className="aig-confirm-btn"
              type="button"
              disabled={selectedItems.length === 0 || confirmLoading}
            >
              {confirmLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Vinculando...
                </>
              ) : (
                <>
                  <FaCheck className="me-1" />
                  Vincular {selectedItems.length > 0 ? `(${selectedItems.length})` : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AddGroupToItemModal;
